import { mutate } from 'swr';

import { Project, ProjectAccessRequest, Team, User } from 'models';
import { ProjectAccessHelpers } from 'helpers/ProjectAccessHelpers';

import { InternalTrackingService } from 'services/InternalTrackingService';
import { ProjectCollaboratorService } from 'services/ProjectCollaboratorService';
import { state as currentTeamState } from 'state/current-team';

import { PayloadSDK } from './PayloadSDK';

const ProjectAccessService = {
    request: async (projectId: Project['id']) => {
        InternalTrackingService.track('project.requestAccess');

        await PayloadSDK.create({
            collection: 'projectAccessRequests',
            data: { project: projectId, status: 'pending' },
        });
    },

    list: async ({
        project,
        team,
        user,
        status,
    }: {
        project?: Project['id'];
        team?: Team['id'];
        user?: User['id'];
        status?: ProjectAccessRequest['status'];
    }) => {
        const { docs } = await PayloadSDK.find({
            collection: 'projectAccessRequests',
            where: {
                ...(project ? { project: { equals: project } } : {}),
                ...(team ? { team: { equals: team } } : {}),
                ...(user ? { createdBy: { equals: user } } : {}),
                ...(status ? { status: { equals: status } } : {}),
            },
            sort: '-createdAt',
            depth: 0,
            pagination: false,
        });

        return docs as ProjectAccessRequest[];
    },

    approve: async (accessRequest: ProjectAccessRequest, permissions: ('project.view' | 'project.edit')[]) => {
        InternalTrackingService.track('project.approveAccessRequest', {
            requester: accessRequest.createdBy,
            permissions,
        });

        await PayloadSDK.update({
            collection: 'projectAccessRequests',
            id: accessRequest.id,
            data: { status: 'approved', permissions },
        });

        await ProjectCollaboratorService.refresh(accessRequest.project);
        await ProjectAccessService.refresh(accessRequest.project);
    },

    deny: async (accessRequest: ProjectAccessRequest) => {
        InternalTrackingService.track('project.denyAccessRequest', {
            requester: accessRequest.createdBy,
        });

        await PayloadSDK.update({
            collection: 'projectAccessRequests',
            id: accessRequest.id,
            data: { status: 'denied' },
        });

        await ProjectAccessService.refresh(accessRequest.project);
    },

    refresh: async (projectId: Project['id']) => {
        await mutate(ProjectAccessHelpers.swr.list(projectId));

        if (currentTeamState.team) {
            await mutate(ProjectAccessHelpers.swr.pendingForTeam(currentTeamState.team.id));
        }
    },
};

export { ProjectAccessService };
